<template>
  <div class="animated fadeIn">
    <b-card>
      <h4 v-if="isUpdateForm">{{ $t('json_schema.update_title') }}</h4>
      <h4 v-else>{{ $t('json_schema.create_title') }}</h4>
      <p>{{ $dt('json_schema.usage_description', 'Create json data map from detection json data. Lookup key is json data object key. If you need deeper data use subitems (drag more to right side)') }}</p>
    </b-card>
    <div v-if="spinners.contentIsLoading" class="text-center">
        <b-spinner type="grow" variant="primary"/>
      </div>
      <div v-if="!spinners.contentIsLoading">
        <b-row>
          <b-col>
            <b-card>
              <b-form-group :invalid-feedback="fErr('schema_data.name')">
                <label>{{ $t('json_schema.name') }}</label>
                <b-form-input type="text" :placeholder="$t('json_schema.name_placeholder')"
                  :class="{ 'is-invalid': fErr('schema_data.name') }"
                  v-model="entity.name"/>
              </b-form-group>
              <b-form-group>
                <label>{{ $dt('json_schema.detection_sets', 'Detection Sets') }}</label>
                <treeselect :class="{ 'treeselect-error': fErr('detection_sets') }"
                :options="detectionSets" :multiple="true" :clearable="true"
                  v-model="entity.detection_sets_ids" :value="id"/>
                <span class="error-text">{{ fErr('label_set_id') }}</span>
              </b-form-group>

              <JsonSchemaTree v-if="isUpdateForm" :schema="entity" :tree="schemaTree" @input="onTreeUpdated"/>
              <div v-if="!spinners.contentIsLoading" slot="footer">
                <b-button type="submit" variant="primary" :disabled="spinners.isSaving" @click="save()">
                  <b-spinner v-if="spinners.isSaving" type="grow" small/>
                  {{ $t('global.save') }}
                </b-button>
                <router-view :to="{ name: 'JsonSchemas' }">
                  <b-button type="reset" variant="info" class="ml-2">
                    {{ $t('global.cancel') }}
                  </b-button>
                </router-view>
              </div>
            </b-card>

          </b-col>
          <b-col v-if="isUpdateForm">
            <b-card v-if="dataPreview?.parsed">
              <h5>{{ $dt('json_schema.parsed_data_preview', 'Parsed Data Preview') }}</h5>
              <table>
                <tr v-for="parsed_data in dataPreview.parsed" :key="parsed_data.key">
                  <td class="w-25">
                    {{ parsed_data.name }}:
                  </td>
                  <td class="w-75">
                  <template v-if="parsed_data.type_name === 'Timestamp' || parsed_data.type_name === 'Timestamp Array'">
                    <template v-if="typeof (parsed_data.value) === 'object'">
                      [<span v-for="(arrayitem, index) in parsed_data.value" :key="index">{{ momentf(arrayitem) }}, </span>]
                    </template>
                    <template v-else>
                      {{ momentf(parsed_data.value) }}
                    </template>
                  </template>
                  <template v-else-if="parsed_data.type_name !== 'Object' && parsed_data.type_name !== 'Object Array'">
                    {{ parsed_data.value }}
                  </template>
                  </td>
                </tr>
              </table>
            </b-card>

            <b-card v-if="dataPreview?.raw">
              <h5>{{ $dt('json_schema.raw_data_preview', 'Raw Data Preview') }}</h5>
              <div style="overflow: scroll; max-height: 300px;">
                <pre>{{ dataPreview.raw }}</pre>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </div>
  </div>
</template>

<script>
import merge from 'lodash.merge';
import { mapState } from 'vuex';
import Treeselect from '@riophae/vue-treeselect';
import Services from '@/services/index';
import JsonSchemaTree from './JsonSchemaTree.vue';

export default {
  mounted() {
    this.load();
  },
  components: {
    JsonSchemaTree,
    Treeselect
  },
  data() {
    return {
      spinners: {
        contentIsLoading: false,
        isSaving: false,
      },
      entity: this.value,
      errorMsg: this.errors,
      schemaTree: [],
      flatTree: [],
      schemaTreeChanged: false,
      detectionSets: [],
      dataPreview: null,
    };
  },
  props: {
    // If id === null, form will be considered create form else update
    id: { type: Number, default: null },
    // if title == false to hide header, pass string to override default title
    title: { type: [String, Boolean], default: undefined },
    // pass errors from parent form if embedded
    errors: { type: Object, default() { return {}; } },
    // value is used both as DEFAULTs placeholder and for v-model
    value: {
      type: Object,
      default() {
        return {
          name: '',
        };
      },
    },
  },
  computed: {
    // embedded form will have value passed using v-model, we could also make a custom prop for checking this
    isEmbedded() { return this.$options.propsData.value !== undefined; },
    isUpdateForm() { return this.id !== null; },
    ...mapState('Auth', ['activeProjectId']),
    projectId() { return this.project_id || this.activeProjectId; },
  },
  methods: {
    getForUpdate: Services.jsonSchemas.editData,
    getForCreate: Services.jsonSchemas.createData,
    pushUpdate: Services.jsonSchemas.update,
    pushCreate: Services.jsonSchemas.create,
    load() {
      this.spinners.contentIsLoading = true;
      const action = this.isUpdateForm ? this.getForUpdate : this.getForCreate;
      action(this.projectId, this.$route.params.id).then((data) => {
        this.spinners.contentIsLoading = false;
        this.entity = this.isUpdateForm ? data.data.jsonSchema : { ...this.entity, ...data.data.jsonSchema };

        this.schemaTree = this.isUpdateForm ? data.data.schemaTree : [];

        this.entity.detection_sets_ids = this.entity?.detection_sets?.map((ds) => ds.id);

        this.detectionSets = data.data.detectionSets.map((item) => { return { id: item.id, label: item.name }; });
        this.dataPreview = data.data.dataPreview;
      }).catch((data) => {
        this.spinners.contentIsLoading = false;
        this.errorMsg = data.data ? data.data.errors : {};
      });
    },
    save() {
      if (this.isEmbedded) return;
      this.spinners.isSaving = true;
      const action = this.isUpdateForm ? this.pushUpdate : this.pushCreate;
      const params = {
        schema_data: { ...this.entity },
      };

      if (this.schemaTreeChanged) {
        params.flat_schema_tree = this.flatTree;
      }

      action(this.projectId, params).then((data) => {
        this.spinners.isSaving = false;
        this.alertSuccess();
        merge(this.entity, data.data.jsonSchema); // This will not delete keys but will preserve original object
        merge(this.schemaTree, data.data.jsonSchema.schemaTree);
        this.errorMsg = {};
        this.dataPreview = data.data.dataPreview;
        if (!this.isUpdateForm) {
          // After successful create
          // if (this.$can('edit', 'json_schemas')) {
          this.$router.push({ name: 'JsonSchemaUpdate', params: { id: data.data.jsonSchema.id } });
          // } else { // do not redirect if cannot edit
          //   this.entity = this.$options.props.value.default();
          //   this.load();
          // }
        }
        this.schemaTreeChanged = false;
      }).catch(({ errors, message }) => {
        this.spinners.isSaving = false;
        this.errorMsg = errors;
        console.error(message);
        this.alertError({ text: message });
      });
    },
    onTreeUpdated(tree) {
      this.flatTree = tree;
      this.schemaTreeChanged = true;
    },
  }
};
</script>

<style scoped></style>
